/**
 * Curated vocabulary of tracked user actions (MYAC-717, Layer A).
 * Values are persisted to Firestore `activity_sessions.actions` (nested by tenant/project)
 * and (later) reused as `csm_events` event names.
 *
 * Naming: `<domain>_<action>` snake_case. NEVER rename an existing value once live —
 * it would orphan historical counts. A few early/live names are grandfathered exceptions
 * (`settings_opened`, `plan_page_viewed`, `export_downloaded`).
 *
 * 📖 Human-readable reference: ./csmActions.md — what each event means + where it's emitted.
 * When you add/rename/remove an action here, update csmActions.md in the SAME change.
 */
export enum CsmAction {
  // --- account ---
  AccountFirstLogin = 'account_first_login',
  AccountNotificationsUpdated = 'account_notifications_updated',
  AccountCompanyCreated = 'account_company_created', // the tenant now exists — first countable moment of a new account
  AccountProfileViewed = 'account_profile_viewed', // MYAC-1138: profile-info, profile-notifications
  CompanyProfileViewed = 'company_profile_viewed', // MYAC-1138: settings-company (tenant-scoped)

  // --- dashboard ---
  DashboardViewed = 'dashboard_viewed',

  // --- settings (grandfathered names) ---
  SettingsOpened = 'settings_opened',
  PlanPageViewed = 'plan_page_viewed',

  // --- billing ---
  BillingPortalOpened = 'billing_portal_opened',
  BillingCheckoutStarted = 'billing_checkout_started',
  // MYAC-1138: reactivation/win-back. BUCKETED — see BUCKETED_ACTIONS — because the
  // event is tenant-scoped (no project), so meta.kind would be silently discarded.
  BillingPlanSwitchStarted = 'billing_plan_switch_started', // bucket: targetPlan — the plan enum, low-cardinality
  // Self-serve gated (MYAC-837): fired when a user hits "Contact sales" instead
  // of self-serve checkout. Scoped so CSM can tell upgrade intent from top-up.
  BillingSalesContactUpgrade = 'billing_sales_contact_upgrade',
  BillingSalesContactTopup = 'billing_sales_contact_topup',
  BillingResubscribePromptViewed = 'billing_resubscribe_prompt_viewed', // MYAC-1138: setup-resubscribe (tenant-scoped)

  // --- project ---
  ProjectOpened = 'project_opened',
  ProjectCreated = 'project_created',
  ProjectDocumentationViewed = 'project_documentation_viewed',
  ProjectMemberAdded = 'project_member_added',
  ProjectMemberRoleChanged = 'project_member_role_changed', // meta: { role } — the member's new role
  ProjectSettingsViewed = 'project_settings_viewed', // MYAC-1138: qms-settings, products-settings,
  // regulatory-monitoring-settings — the latter shares this rather than getting its own
  // member; it is a project's settings page like the other two.
  ProjectListViewed = 'project_list_viewed', // MYAC-1138: qms-list, products-list,
  // regulatory-monitoring-list (tenant-scoped). recordActivity has no discriminator for
  // tenant-scoped actions, so the three rosters cannot be told apart — accepted, the
  // question this answers ("does this user ever browse a roster") doesn't need the split.
  ProjectAdminViewed = 'project_admin_viewed', // MYAC-1138: settings-projects (tenant-scoped)

  // --- team ---
  TeamMemberInvited = 'team_member_invited',
  TeamMemberRemoved = 'team_member_removed', // tenant-scoped
  TeamInvitationCancelled = 'team_invitation_cancelled', // tenant-scoped
  TeamInvitationProjectsUpdated = 'team_invitation_projects_updated', // MYAC-1176, tenant-scoped
  TeamInvitationAccepted = 'team_invitation_accepted', // tenant-scoped. KNOWN LIMIT: attributed to the
  // CURRENTLY SELECTED tenant. For a user who already belongs to another tenant, the
  // selection does not switch on join, so the event lands on the previous tenant.
  // trackAction has no explicit-tenant override — see MYAC-1138 Tier 4.
  TeamViewed = 'team_viewed', // MYAC-1138: settings-users (tenant-scoped)

  // --- assessment ---
  AssessmentStarted = 'assessment_started',
  AssessmentViewed = 'assessment_viewed',
  AssessmentScheduled = 'assessment_scheduled',

  // --- change analysis (MYAC-1138) ---
  // Named for the product surface (`/change-analysis`), not the internal
  // `changeControl` store. There is no separate "updated" event: after creation a
  // change analysis only evolves through the specialist chat.
  ChangeAnalysisCreated = 'change_analysis_created',
  ChangeAnalysisMessageSent = 'change_analysis_message_sent', // meta: { kind } — message | clarification.
  // This is the "updated" signal for a change analysis: the store has no edit action,
  // so a change only evolves through this conversation.
  ChangeAnalysisExported = 'change_analysis_exported', // MYAC-1180: the analysis left the app as a PDF record
  ChangeAnalysisReadRetried = 'change_analysis_read_retried', // friction signal, not adoption
  ChangeAnalysisViewed = 'change_analysis_viewed', // MYAC-1138: products-change-control

  // --- risk analysis (MYAC-1237) ---
  // Frontend-only, no backend mirror: the risk register's own activity is not
  // tracked yet — this is the same "did anyone open the surface" moment the
  // other proposed-view members record.
  RiskAnalysisViewed = 'risk_analysis_viewed', // MYAC-1138-style: products-risk-analysis

  // --- gap analysis (MYAC-776) ---
  GapAnalysisStarted = 'gap_analysis_started', // a run was requested on a target project
  GapAnalysisViewed = 'gap_analysis_viewed', // products-gap-analysis / qms-gap-analysis
  GapAnalysisExported = 'gap_analysis_exported', // the analysis left the app as a PDF record
  GapAnalysisMessageSent = 'gap_analysis_message_sent', // meta: { kind } — message | clarification
  GapAnalysisChangeCreated = 'gap_analysis_change_created', // one impacted file handed off to change control
  GapAnalysisReadRetried = 'gap_analysis_read_retried', // friction signal, not adoption

  // --- referential (MYAC-1138) ---
  // A referential is the standard/regulation pack a project is assessed against.
  // Neither `assessment` (running one) nor `document` (the corpus) names the pack
  // itself, so this is its own domain.
  // BUCKETED: the counter leaf is a map of `<referential_id>:<stage>` → count.
  ReferentialAdopted = 'referential_adopted',
  ReferentialRemoved = 'referential_removed',
  ReferentialRunResumed = 'referential_run_resumed', // un-parking a stalled run rather than abandoning it
  ReferentialConsistencySwept = 'referential_consistency_swept', // the paid cross-document sweep, run deliberately
  ReferentialStageDeclared = 'referential_stage_declared', // meta: { status } — stage1 | stage2. QMS only.
  ReferentialsViewed = 'referentials_viewed', // MYAC-1138: qms-referentials, products-referentials

  // --- register (MYAC-1138) ---
  // The QMS registers (CAPA, NC, change control, supplier) are extracted from the
  // document corpus by the engine. Emitted from the shared factory, so every
  // register — including any added later — is instrumented by construction.
  RegisterExtractionTriggered = 'register_extraction_triggered', // meta: { kind } — capa | nc | change_control | supplier
  RegisterViewed = 'register_viewed', // MYAC-1138: qms-registers

  // --- audit (internal audit) ---
  AuditStarted = 'audit_started',
  AuditViewed = 'audit_viewed',
  AuditExported = 'audit_exported',
  AuditAiRequested = 'audit_ai_requested', // meta: { kind } — grid | audit_ai | ccapa | strengths

  // --- task ---
  TaskViewed = 'task_viewed',
  TaskCompleted = 'task_completed', // ⭐ kept — the "remediation done" milestone
  TaskAccepted = 'task_accepted', // meta: { count } on bulk-accept
  TaskRejected = 'task_rejected',
  TaskAssigned = 'task_assigned', // meta: { assigned } — false = unassigned
  TaskStatusChanged = 'task_status_changed', // meta: { status } — fires on every transition (incl. done)

  // --- directive (task directives, IR-109) ---
  DirectiveCreated = 'directive_created', // reject-with-directive confirmed
  DirectiveOverridden = 'directive_overridden', // "Accept anyway" on an auto-rejected task
  DirectiveDeleted = 'directive_deleted',
  DirectiveToggled = 'directive_toggled', // activated/deactivated
  DirectiveViewed = 'directive_viewed', // MYAC-1138: qms-directives, products-directives

  // --- report (grandfathered: export_downloaded) ---
  ReportViewed = 'report_viewed',
  ExportDownloaded = 'export_downloaded',

  // --- document ---
  DocumentUploaded = 'document_uploaded',

  // --- datasource ---
  DatasourceConnected = 'datasource_connected',
  DatasourceResynced = 'datasource_resynced',
  DatasourceDeleted = 'datasource_deleted',

  // --- chat ---
  ChatOpened = 'chat_opened',
  ChatMessageSent = 'chat_message_sent',
  ChatActionApproved = 'chat_action_approved', // meta: { kind } — the proposed action, from a closed set
  ChatActionDeclined = 'chat_action_declined', // meta: { kind } — same discriminator
  ChatSessionReset = 'chat_session_reset', // "New session" — a user-initiated restart, never the internal reset a store call runs mid-flow to supersede its own turn
  ChatPromptReused = 'chat_prompt_reused', // NO meta: a saved prompt's name and body are tenant-authored free text
  ChatFocusAttached = 'chat_focus_attached', // meta: { kind } ONLY — ChatFocusRef also carries uid/code/name/label, none of which may be persisted
  ChatRetried = 'chat_retried', // "Yes, try again" after a failed turn — reliability, not engagement

  // --- td ---
  TdModuleOpened = 'td_module_opened',
  TdScheduled = 'td_scheduled',
  TdSectionRerun = 'td_section_rerun', // single-section rerun. NAME IS GRANDFATHERED:
  // since MYAC-1138 this also fires for QMS scoped re-runs through the referentials
  // engine. Renaming would orphan historical counts, so the doc carries the truth.

  // --- regulation ---
  RegulationReportViewed = 'regulation_report_viewed',
  RegulationNormsViewed = 'regulation_norms_viewed',
  RegulationProfileCreated = 'regulation_profile_created', // meta: { kind } — company | device. Fires
  // on the create command's LANDED (`done`) outcome via `_trackCommand`'s `onLanded`
  // hook, same as `regulation_norm_added` — never on the write merely being dispatched.
  RegulationProfileGenerated = 'regulation_profile_generated', // AI drafted the profile from a linked
  // project. Same LANDED (`done`) choke point as regulation_profile_created — never dispatch.
  RegulationProfileEdited = 'regulation_profile_edited', // NO meta — the profile body is client free text
  RegulationProfileDeleted = 'regulation_profile_deleted',
  RegulationProfileViewed = 'regulation_profile_viewed', // MYAC-1138: regulatory-monitoring-profile
  RegulationSuggestionsViewed = 'regulation_suggestions_viewed', // MYAC-1307: regulatory-monitoring-suggestions, the page the Suggested tab became
  RegulationSuggestionsRun = 'regulation_suggestions_run', // paid, profile-grounded discovery. Distinct from
  // regulation_watch_triggered, which re-checks norms already being watched.
  RegulationProjectConnected = 'regulation_project_connected', // a QMS/TD project linked to a monitoring
  // profile. Fires off the link store's own `lastCommandStatus === 'done'` watch, not on the write's admission.
  RegulationConnectTdWall = 'regulation_connect_td_wall', // MYAC-1148: bare counter, project-scoped. Fires
  // on the CLICK of « Connecter ma TD » for a free-veille tenant, BEFORE the Contact-sales dialog opens —
  // measuring intent, not conversion, deliberately unlike the LANDED-choke-point events around it.
  RegulationNormAdded = 'regulation_norm_added', // meta: { kind } — 'manual' | 'suggested' (MYAC-1138: also fires for AI-suggested adds, split by kind)
  RegulationNormsUploaded = 'regulation_norms_uploaded', // file upload → backend extract
  RegulationWatchTriggered = 'regulation_watch_triggered',
  RegulationWatchConfigured = 'regulation_watch_configured',
  RegulationMarkedSeen = 'regulation_marked_seen',
  RegulationCommentAdded = 'regulation_comment_added',
  RegulationReportExported = 'regulation_report_exported',
  // MYAC-1119. Fires when a user submits two editions for comparison, not when the
  // resulting report is opened — the paid work is the run, and it is the run whose
  // frequency tells us whether the feature is used.
  RegulationRedlineStarted = 'regulation_redline_started',
}
